// src/views/hrms/employees/EmployeeDetail.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Loading from "components/common/Loading";
import {
  getEmployeeById,
  getAttendanceByEmployee,
  getLiveLocations,
  getOfficeByTenant,
  getEmployeeCurrentShift, // ✅ NEW
} from "../../../services/hrms.api";
import { ChevronLeft, MapPin, Clock } from "lucide-react";

/* ---------- helpers ---------- */
const pickId = (x) => String(x?.id || x?._id || x?.employeeId || x?.employee || "");

const titleCase = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1).toLowerCase() : s);

const time12ToMinutes = (t) => {
  if (!t) return -1;
  const m = String(t).trim().match(/^(\d{1,2}):(\d{2})(?::\d{2})?\s*([AP]M)$/i);
  if (!m) return -1;
  let hh = parseInt(m[1], 10);
  const mm = parseInt(m[2], 10);
  const ap = m[3].toUpperCase();
  if (ap === "PM" && hh !== 12) hh += 12;
  if (ap === "AM" && hh === 12) hh = 0;
  return hh * 60 + mm;
};

const localMidnight = (dLike) => {
  const d = new Date(dLike);
  return new Date(d.getFullYear(), d.getMonth(), d.getDate(), 0, 0, 0, 0);
};

const minsBetween = (a, b) =>
  a instanceof Date && b instanceof Date && !isNaN(a) && !isNaN(b) && b > a
    ? Math.round((b - a) / 60000)
    : 0;

const fmt12 = (dt) =>
  dt instanceof Date && !isNaN(dt)
    ? dt.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit" })
    : "-";

const fmtHM = (m) =>
  Number.isFinite(m) && m > 0
    ? `${String(Math.floor(m / 60)).padStart(2, "0")}h ${String(m % 60).padStart(2, "0")}m`
    : "-";

const getEmpName = (e, fallbackId = "") => {
  if (!e) return fallbackId || "Employee";
  if (e.name) return e.name;
  const full = `${e.firstName || ""} ${e.lastName || ""}`.trim();
  const shortId = fallbackId ? `${fallbackId.slice(0, 6)}…${fallbackId.slice(-4)}` : "";
  return full || e.code || e.username || e.email || shortId || "Employee";
};

const haversineKm = (a, b) => {
  if (
    !a ||
    !b ||
    !Number.isFinite(a.lat) ||
    !Number.isFinite(a.lng) ||
    !Number.isFinite(b.lat) ||
    !Number.isFinite(b.lng)
  ) return Infinity;

  const R = 6371;
  const dLat = ((b.lat - a.lat) * Math.PI) / 180;
  const dLng = ((b.lng - a.lng) * Math.PI) / 180;
  const la1 = (a.lat * Math.PI) / 180;
  const la2 = (b.lat * Math.PI) / 180;
  const s1 = Math.sin(dLat / 2);
  const s2 = Math.sin(dLng / 2);
  return 2 * R * Math.asin(Math.sqrt(s1 * s1 + Math.cos(la1) * Math.cos(la2) * s2 * s2));
};

const msToMin = (ms) => (Number.isFinite(ms) ? Math.max(0, Math.round(ms / 60000)) : NaN);

const computeFromAttendanceDoc = (doc) => {
  const workDay = doc?.workDay || doc?.date || doc?.day || doc?.createdAt || Date.now();
  const day0 = localMidnight(workDay);

  const sessions = Array.isArray(doc?.sessions) ? doc.sessions : [];
  const breaks = Array.isArray(doc?.breaks) ? doc.breaks : [];

  const firstSession = sessions[0];
  const lastSession = sessions[sessions.length - 1];

  const rawIn = doc?.clockIn || firstSession?.in || null;
  const rawOut = doc?.clockOut || lastSession?.out || null;

  let inTime = rawIn ? new Date(rawIn) : null;
  let outTime = rawOut ? new Date(rawOut) : null;

  let workedMin = msToMin(doc?.workedMs);
  let breakMin = msToMin(doc?.breakMs);
  if (!Number.isFinite(workedMin)) workedMin = NaN;
  if (!Number.isFinite(breakMin)) breakMin = 0;

  if (!Number.isFinite(workedMin)) {
    const closedSessions = sessions.filter((s) => s.in && s.out);
    const sessionMin = closedSessions.reduce(
      (acc, s) => acc + minsBetween(new Date(s.in), new Date(s.out)),
      0
    );

    const closedBreakMin = breaks.reduce((acc, b) => {
      const start = b?.start ? new Date(b.start) : null;
      const end = b?.end ? new Date(b.end) : null;
      return acc + minsBetween(start, end);
    }, 0);

    const state = String(doc?.state || "").toUpperCase();
    const isLive = state === "IN";
    let liveMin = 0;

    if (isLive) {
      const open =
        sessions.findLast?.((s) => s.in && !s.out) ||
        [...sessions].reverse().find((s) => s.in && !s.out);
      if (open?.in) liveMin = minsBetween(new Date(open.in), new Date());
    }

    workedMin = Math.max(0, sessionMin + liveMin - closedBreakMin);
    breakMin = closedBreakMin;
  }

  const backendStatus = doc.status ? titleCase(doc.status) : "";
  const state = String(doc?.state || "").toUpperCase();
  const isLive = state === "IN" || state === "BREAK";

  const status = backendStatus || (isLive || workedMin > 0 || inTime ? "Present" : "Absent");

  return {
    date: `${day0.getFullYear()}-${String(day0.getMonth() + 1).padStart(2, "0")}-${String(
      day0.getDate()
    ).padStart(2, "0")}`,
    inTime,
    outTime,
    _mins: workedMin,
    _breakMin: breakMin,
    status,
  };
};

export default function EmployeeDetail() {
  const { id } = useParams();
  const nav = useNavigate();

  const [emp, setEmp] = useState(null);
  const [currentAssign, setCurrentAssign] = useState(null); // ✅ NEW
  const [attn, setAttn] = useState([]);
  const [gpsPoint, setGpsPoint] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [officeCenter, setOfficeCenter] = useState(null);

  // pagination
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(20);

  // calendar filter
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [datePreset, setDatePreset] = useState("all");

  const NEAR_KM = 0.5,
    CLOSE_KM = 1.5,
    MOD_KM = 5;

  const User = localStorage.getItem("user");
  const tenantUser = JSON.parse(User || "{}");
  const tenantId = tenantUser?.tenant;

  // office center for distance calc (fallback)
  useEffect(() => {
    (async () => {
      try {
        const { data } = await getOfficeByTenant(tenantId);
        const items = data?.items || [];
        const primary = items[0];
        if (primary && Number.isFinite(primary.lat) && Number.isFinite(primary.lng)) {
          setOfficeCenter({ lat: primary.lat, lng: primary.lng });
        }
      } catch (err) {
        console.error("Failed to load office center", err);
      }
    })();
  }, [tenantId]);

  useEffect(() => {
    let alive = true;

    (async () => {
      try {
        setLoading(true);
        setError("");

        // 1) employee
        let employee = null;
        try {
          const { data: empRaw } = await getEmployeeById(id);
          if (!alive) return;
          if (empRaw) employee = { ...empRaw, _normId: pickId(empRaw) };
        } catch {
          // ignore
        }

        // ✅ 2) current shift assignment (office + shift)
        try {
          const { data: cs } = await getEmployeeCurrentShift(id);
          if (!alive) return;
          setCurrentAssign(cs || null);

          // if assignment office has lat/lng, prefer it as center
          const off = cs?.office;
          if (off && Number.isFinite(off.lat) && Number.isFinite(off.lng)) {
            setOfficeCenter({ lat: off.lat, lng: off.lng });
          }
        } catch (e) {
          setCurrentAssign(null);
        }

        // 3) attendance
        const { data: a } = await getAttendanceByEmployee(id);
        if (!alive) return;
        const list = Array.isArray(a) ? a : a?.items || [];

        const normalized = list
          .map((doc) => {
            const comp = computeFromAttendanceDoc(doc);

            let inTime = comp.inTime;
            let outTime = comp.outTime;

            if (!inTime && doc.in) {
              const m = time12ToMinutes(doc.in);
              if (m >= 0)
                inTime = new Date(localMidnight(doc.workDay || doc.date || Date.now()).getTime() + m * 60000);
            }
            if (!outTime && doc.out) {
              const m = time12ToMinutes(doc.out);
              if (m >= 0)
                outTime = new Date(localMidnight(doc.workDay || doc.date || Date.now()).getTime() + m * 60000);
            }

            const mins =
              Number.isFinite(comp._mins) && comp._mins > 0 ? comp._mins : minsBetween(inTime, outTime);

            const status = doc.status ? titleCase(doc.status) : comp.status;

            return {
              _id: pickId(doc),
              date: comp.date,
              inTime,
              outTime,
              _mins: mins,
              _breakMin: comp._breakMin,
              status,
              employeeId: pickId(doc.employee || doc),
              employeeName: doc.employeeName,
            };
          })
          .sort((a, b) => new Date(b.date) - new Date(a.date));

        setAttn(normalized);

        // 4) ghost employee fallback
        if (!employee) {
          const first = normalized[0];
          const ghostId = id;
          const ghostName =
            (first && first.employeeName) ||
            (ghostId.length > 14 ? `${ghostId.slice(0, 6)}…${ghostId.slice(-4)}` : ghostId);

          employee = {
            _normId: ghostId,
            name: ghostName,
            status: "active",
            department: "-",
            designation: "Employee",
            email: "-",
            phone: "-",
            joinDate: null,
            __ghost: true,
          };
        }

        setEmp(employee);

        // 5) gps
        const { data: g } = await getLiveLocations();
        if (!alive) return;
        const gpsListRaw = Array.isArray(g) ? g : g?.items || [];
        const gpsList = gpsListRaw.map((p) => {
          const coords = Array.isArray(p.location?.coordinates) ? p.location.coordinates : [null, null];
          const [lng, lat] = coords;
          return {
            ...p,
            employeeId: String(p.employee || p.employeeId || ""),
            lat,
            lng,
            ts: new Date(p.capturedAt || p.receivedAt || p.updatedAt || p.createdAt || 0).getTime(),
          };
        });

        const latest = gpsList
          .filter((p) => String(p.employeeId) === String(employee._normId))
          .sort((a, b) => b.ts - a.ts)[0];

        setGpsPoint(latest || null);
      } catch (e) {
        console.error("[EmployeeDetail] load error:", e);
        if (alive) setError(e?.message || "Failed to load employee.");
      } finally {
        if (alive) setLoading(false);
      }
    })();

    return () => {
      alive = false;
    };
  }, [id]);

  const applyDatePreset = (preset) => {
    setDatePreset(preset);
    const today = new Date();
    const toISO = (d) => d.toISOString().slice(0, 10);

    if (preset === "all") {
      setFromDate("");
      setToDate("");
      return;
    }
    if (preset === "last7") {
      const from = new Date(today);
      from.setDate(from.getDate() - 6);
      setFromDate(toISO(from));
      setToDate(toISO(today));
      return;
    }
    if (preset === "last30") {
      const from = new Date(today);
      from.setDate(from.getDate() - 29);
      setFromDate(toISO(from));
      setToDate(toISO(today));
      return;
    }
    if (preset === "thisMonth") {
      const from = new Date(today.getFullYear(), today.getMonth(), 1);
      const to = new Date(today.getFullYear(), today.getMonth() + 1, 0);
      setFromDate(toISO(from));
      setToDate(toISO(to));
      return;
    }
  };

  const joinText = useMemo(() => {
    try {
      return emp?.joinDate
        ? new Date(emp.joinDate).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" })
        : "-";
    } catch {
      return "-";
    }
  }, [emp?.joinDate]);

  const dob = useMemo(() => {
    try {
      return emp?.dob
        ? new Date(emp.dob).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" })
        : "-";
    } catch {
      return "-";
    }
  }, [emp?.dob]);

  const distanceInfo = useMemo(() => {
    if (!gpsPoint) return { label: "No GPS", km: null, tone: "bg-gray-100 text-gray-700" };
    if (!officeCenter) return { label: "No Office", km: null, tone: "bg-gray-100 text-gray-700" };

    const km = haversineKm({ lat: gpsPoint.lat, lng: gpsPoint.lng }, officeCenter);

    const tone =
      km <= NEAR_KM
        ? "bg-emerald-100 text-emerald-700"
        : km <= CLOSE_KM
        ? "bg-blue-100 text-blue-700"
        : km <= MOD_KM
        ? "bg-amber-100 text-amber-700"
        : "bg-rose-100 text-rose-700";

    const label =
      km <= NEAR_KM ? "In Office" : km <= CLOSE_KM ? "Nearby" : km <= MOD_KM ? "Moderate" : "Far";

    return { label, km, tone };
  }, [gpsPoint, officeCenter]);

  const salaryInfo = useMemo(() => {
    const raw = typeof emp?.monthlySalary === "number" ? emp.monthlySalary : emp?.salary;
    const salaryNum = Number(raw);

    if (!Number.isFinite(salaryNum) || salaryNum <= 0) return { hasSalary: false, text: "Not Assigned" };

    const perDay = salaryNum / 30;
    const monthlyText = `₹${salaryNum.toLocaleString("en-IN")}`;
    const perDayText = `₹${Number(perDay.toFixed(0)).toLocaleString("en-IN")}`;

    return { hasSalary: true, text: `${monthlyText} / month (${perDayText} per day)` };
  }, [emp?.monthlySalary, emp?.salary]);

  const filteredAttn = useMemo(() => {
    if (!fromDate && !toDate) return attn;
    return attn.filter((r) => {
      if (!r.date) return false;
      const dayStr = String(r.date).slice(0, 10);
      if (fromDate && dayStr < fromDate) return false;
      if (toDate && dayStr > toDate) return false;
      return true;
    });
  }, [attn, fromDate, toDate]);

  const totalPages = Math.max(1, Math.ceil(filteredAttn.length / perPage) || 1);
  const start = (page - 1) * perPage;
  const end = page * perPage;
  const pageRows = filteredAttn.slice(start, end);

  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [totalPages, page]);

  // ✅ current shift display
  const shiftText = useMemo(() => {
    const s = currentAssign?.shift;
    if (!s) return "-";
    return `${s.name} (${s.startTime} - ${s.endTime})`;
  }, [currentAssign]);

  const officeText = useMemo(() => {
    const o = currentAssign?.office;
    if (!o) return "-";
    const city = o.city ? `, ${o.city}` : "";
    return `${o.name || "Office"}${city}`;
  }, [currentAssign]);

  if (loading) return <Loading variant="dots" fullscreen />;

  if (error) {
    return (
      <div className="p-6">
        <div className="mb-4 rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-700 dark:border-red-400/30 dark:bg-red-400/10">
          {error}
        </div>
        <button
          onClick={() => nav("/admin/hrms/employees")}
          className="flex items-center gap-1 rounded-lg border px-3 py-1.5 text-sm hover:bg-gray-50 dark:border-white/10 dark:text-white"
        >
          <ChevronLeft className="h-4 w-4 " /> Back
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <button
            onClick={() => nav(-1)}
            className="hover:bg-black flex items-center gap-1 rounded-lg border px-3 py-1.5 text-sm dark:border-white/10 dark:text-white"
          >
            <ChevronLeft className="h-4 w-4" /> Back
          </button>
          <h2 className="text-2xl font-semibold text-gray-800 dark:text-white">Employees</h2>
        </div>
      </div>

      {/* Profile Card */}
      <div className="flex flex-col items-center gap-6 rounded-2xl border border-gray-200 bg-white p-6 dark:border-white/10 dark:bg-navy-800 sm:flex-row sm:items-start">
        <div className="flex h-24 w-24 items-center justify-center rounded-full bg-gray-200 text-2xl font-semibold text-gray-600">
          {getEmpName(emp, emp?._normId).charAt(0)}
        </div>

        <div className="flex-1">
          <div className="flex flex-col justify-between sm:flex-row sm:items-center">
            <div>
              <h3 className="text-xl font-semibold text-gray-800 dark:text-white">{getEmpName(emp, emp?._normId)}</h3>
              <p className="text-sm text-gray-500">
                {emp?.designation || emp?.role || "Employee"} • {emp?.department || "General"}
                {emp?.__ghost ? " (no profile on file)" : ""}
              </p>

              {/* ✅ Shift + Office chips */}
              <div className="mt-2 flex flex-wrap gap-2">
                <span className="inline-flex items-center gap-1 rounded-full bg-gray-100 px-3 py-1 text-xs font-medium text-gray-700">
                  <Clock className="h-3 w-3" />
                  {shiftText}
                </span>
                <span className="inline-flex items-center gap-1 rounded-full bg-gray-100 px-3 py-1 text-xs font-medium text-gray-700">
                  <MapPin className="h-3 w-3" />
                  {officeText}
                </span>
              </div>
            </div>

            <div className="flex items-center gap-2 dark:text-white">
              {gpsPoint && (
                <span
                  className={`inline-flex items-center gap-1 rounded-full px-3 py-1 text-xs font-medium ${distanceInfo.tone}`}
                  title={
                    gpsPoint && Number.isFinite(distanceInfo.km) ? `${distanceInfo.km.toFixed(2)} km from office` : ""
                  }
                >
                  <MapPin className="h-3 w-3" />
                  {distanceInfo.label}
                </span>
              )}

              <span
                className={`rounded-full px-3 py-1 text-xs font-medium ${
                  (emp?.status || "active").toLowerCase() === "inactive"
                    ? "bg-red-100 text-red-700"
                    : "bg-green-100 text-green-700"
                }`}
              >
                {(emp?.status || "active").toLowerCase() === "inactive" ? "Inactive" : "Active"}
              </span>
            </div>
          </div>

          <div className="mt-4 grid grid-cols-1 gap-x-8 gap-y-3 text-sm text-gray-600 dark:text-gray-300 sm:grid-cols-2">
            <DetailItem label="Employee ID" value={emp?.code || "-"} />
            <DetailItem label="Username" value={emp?.username || "-"} />
            <DetailItem label="Joining Date" value={joinText || "-"} />
            <DetailItem label="Date Of Birth" value={dob || "-"} />
            <DetailItem label="Email-Id" value={emp?.email || "-"} />
            <DetailItem label="Phone Number" value={emp?.phone || "-"} />
            <DetailItem label="Aadhar Number" value={emp?.aadhaarNumber || "-"} />
            <DetailItem label="Pan Number" value={emp?.panNumber || "-"} />
            <DetailItem label="Department" value={emp?.department || "-"} />
            <DetailItem label="Address" value={emp?.address || "-"} />
            <DetailItem label="Salary" value={salaryInfo.text} />
          </div>
        </div>
      </div>

      {/* Attendance */}
      <div className="rounded-2xl border border-gray-200 bg-white p-6 dark:border-white/10 dark:bg-navy-800">
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
          <h4 className="text-lg font-semibold text-gray-800 dark:text-white">Attendance</h4>

          <div className="flex flex-wrap items-center gap-3 text-sm">
            <div className="flex items-center gap-2">
              <input
                type="date"
                className="rounded-lg border border-gray-300 px-3 py-1.5 text-sm dark:border-white/10 dark:bg-navy-800 dark:text-white"
                value={fromDate}
                onChange={(e) => {
                  setFromDate(e.target.value);
                  setDatePreset("custom");
                  setPage(1);
                }}
              />
              <span className="text-gray-500 dark:text-gray-400">to</span>
              <input
                type="date"
                className="rounded-lg border border-gray-300 px-3 py-1.5 text-sm dark:border-white/10 dark:bg-navy-800 dark:text-white"
                value={toDate}
                onChange={(e) => {
                  setToDate(e.target.value);
                  setDatePreset("custom");
                  setPage(1);
                }}
              />
            </div>

            <select
              className="rounded-lg border border-gray-300 px-3 py-1.5 text-sm dark:border-white/10 dark:bg-navy-800 dark:text-white"
              value={datePreset}
              onChange={(e) => {
                applyDatePreset(e.target.value);
                setPage(1);
              }}
            >
              <option value="all">All Time</option>
              <option value="last7">Last 7 Days</option>
              <option value="last30">Last 30 Days</option>
              <option value="thisMonth">This Month</option>
              <option value="custom">Custom (Calendar)</option>
            </select>

            <div className="flex items-center gap-2">
              <span className="dark:text-white">Rows</span>
              <select
                value={perPage}
                onChange={(e) => {
                  setPerPage(+e.target.value);
                  setPage(1);
                }}
                className="dark:bg-transparent rounded-lg border px-2 py-1 dark:border-white/10 dark:bg-navy-800 dark:text-white"
              >
                {[10, 20, 50, 100].map((n) => (
                  <option key={n} value={n}>
                    {n}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full text-left text-sm">
            <thead className="bg-gray-50 dark:bg-white/5">
              <tr>
                <th className="px-4 py-2 dark:text-gray-500">Date</th>
                <th className="px-4 py-2 dark:text-gray-500">Punch In</th>
                <th className="px-4 py-2 dark:text-gray-500">Punch Out</th>
                <th className="px-4 py-2 dark:text-gray-500">Hours</th>
                <th className="px-4 py-2 dark:text-gray-500">Break</th>
                <th className="px-4 py-2 dark:text-gray-500">Status</th>
              </tr>
            </thead>

            <tbody>
              {pageRows.length === 0 ? (
                <tr>
                  <td colSpan="6" className="px-4 py-6 text-center text-gray-500 dark:text-white">
                    No records found.
                  </td>
                </tr>
              ) : (
                pageRows.map((r) => {
                  const prettyDate = new Date(r.date).toLocaleDateString("en-GB", {
                    day: "2-digit",
                    month: "short",
                    year: "numeric",
                  });

                  return (
                    <tr key={r._id} className="border-t border-gray-100 dark:border-white/10">
                      <td className="px-4 py-2 dark:text-white">{prettyDate}</td>
                      <td className="px-4 py-2 dark:text-white">{fmt12(r.inTime)}</td>
                      <td className="px-4 py-2 dark:text-white">{fmt12(r.outTime)}</td>
                      <td className="px-4 py-2 dark:text-white">{fmtHM(r._mins)}</td>
                      <td className="px-4 py-2 dark:text-white">{fmtHM(r._breakMin)}</td>
                      <td className="px-4 py-2 dark:text-white">
                        <span
                          className={`rounded-full px-2 py-1 text-xs font-medium ${
                            r.status === "Present"
                              ? "bg-green-100 text-green-700"
                              : r.status === "Absent"
                              ? "bg-red-100 text-red-700"
                              : "bg-gray-100 text-gray-700"
                          }`}
                        >
                          {r.status}
                        </span>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="mt-4 flex items-center justify-between text-sm text-gray-600 dark:text-gray-300">
          <div>
            Showing <span className="font-medium">{filteredAttn.length ? start + 1 : 0}</span> to{" "}
            <span className="font-medium">{Math.min(end, filteredAttn.length)}</span> of{" "}
            <span className="font-medium">{filteredAttn.length}</span> entries
          </div>

          <div className="flex items-center gap-2">
            <button
              className="rounded-lg border px-3 py-1 disabled:opacity-50"
              disabled={page === 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
            >
              Prev
            </button>
            <span>
              Page {page} / {totalPages}
            </span>
            <button
              className="rounded-lg border px-3 py-1 disabled:opacity-50"
              disabled={page === totalPages}
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            >
              Next
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function DetailItem({ label, value }) {
  return (
    <div>
      <p className="text-xs uppercase text-gray-400">{label}</p>
      <p className="font-medium text-gray-700 dark:text-white">{value ?? "-"}</p>
    </div>
  );
}
